/**
 * 3D representation of any drill equipment variant. Switches over
 * `cone.variant` and emits a simplified mesh family. 15 source variants
 * collapse to 4 mesh families to keep the file under a couple hundred
 * lines + the runtime tree shallow.
 */
import { useMemo } from 'react';
import * as THREE from 'three';
import type { DrillCone } from '@4cm/core/drills/types';

interface Props {
  cone: DrillCone;
  worldPosition: { x: number; z: number };
}

const DEFAULT_CONE_COLOR = '#F39C12';
const DEFAULT_MANNEQUIN_COLOR = '#95A5A6';
const DEFAULT_GOAL_COLOR = '#FFFFFF';
const DEFAULT_OBSTACLE_COLOR = '#9B59B6';
const DEFAULT_FLAG_COLOR = '#E74C3C';

export function DrillCone3D({ cone, worldPosition }: Props): React.JSX.Element {
  const variant = cone.variant ?? 'cone';
  const rotationY = (cone.rotation ?? 0) * (Math.PI / 180);
  const position = useMemo<[number, number, number]>(
    () => [worldPosition.x, 0, worldPosition.z],
    [worldPosition.x, worldPosition.z],
  );

  // Goals + crossbars — frame mesh.
  if (variant.startsWith('goal_') || variant === 'crossbar') {
    return (
      <group position={position} rotation={[0, rotationY, 0]} userData={{ kind: 'goal', variant }}>
        <GoalFrame variant={variant} color={cone.color ?? DEFAULT_GOAL_COLOR} />
      </group>
    );
  }

  // Mannequin / pole — tall capsule.
  if (variant === 'mannequin' || variant === 'pole') {
    const height = variant === 'mannequin' ? 1.8 : 1.6;
    const radius = variant === 'mannequin' ? 0.25 : 0.08;
    return (
      <group position={[position[0], height / 2, position[2]]} rotation={[0, rotationY, 0]} userData={{ kind: 'mannequin', variant }}>
        <mesh castShadow>
          <capsuleGeometry args={[radius, height - radius * 2, 4, 12]} />
          <meshStandardMaterial color={cone.color ?? DEFAULT_MANNEQUIN_COLOR} roughness={0.6} />
        </mesh>
      </group>
    );
  }

  // Hurdle / ladder / bench / rebounder — simplified box meshes.
  if (variant === 'hurdle_mini' || variant === 'hurdle_med' || variant === 'agility_ladder' || variant === 'bench' || variant === 'rebounder') {
    return (
      <group position={position} rotation={[0, rotationY, 0]} userData={{ kind: 'obstacle', variant }}>
        <ObstacleMesh variant={variant} color={cone.color ?? DEFAULT_OBSTACLE_COLOR} />
      </group>
    );
  }

  // Gate — two cones with a marker line.
  if (variant === 'gate') {
    const half = 0.6;
    return (
      <group position={position} rotation={[0, rotationY, 0]} userData={{ kind: 'gate', variant }}>
        <ConeMesh offset={[-half, 0, 0]} color={cone.color ?? DEFAULT_CONE_COLOR} />
        <ConeMesh offset={[half, 0, 0]} color={cone.color ?? DEFAULT_CONE_COLOR} />
      </group>
    );
  }

  // Corner flag — pole + flag plane.
  if (variant === 'corner_flag') {
    return (
      <group position={position} rotation={[0, rotationY, 0]} userData={{ kind: 'flag', variant }}>
        <mesh position={[0, 0.75, 0]} castShadow>
          <cylinderGeometry args={[0.04, 0.04, 1.5, 8]} />
          <meshStandardMaterial color="#bdc3c7" roughness={0.6} />
        </mesh>
        <mesh position={[0.3, 1.35, 0]} castShadow>
          <planeGeometry args={[0.6, 0.3]} />
          <meshStandardMaterial color={cone.color ?? DEFAULT_FLAG_COLOR} side={THREE.DoubleSide} />
        </mesh>
      </group>
    );
  }

  // Cone family (cone / cone_disc / flat / cone_training / cone_traffic).
  return (
    <group position={position} rotation={[0, rotationY, 0]} userData={{ kind: 'cone', variant }}>
      <ConeMesh
        offset={[0, 0, 0]}
        color={cone.color ?? DEFAULT_CONE_COLOR}
        variant={variant}
      />
    </group>
  );
}

// ---- Sub-meshes ------------------------------------------------------------

function ConeMesh({ offset, color, variant }: { offset: [number, number, number]; color: string; variant?: string }): React.JSX.Element {
  const isFlat = variant === 'flat' || variant === 'cone_disc';
  const isLarge = variant === 'cone_traffic';
  const height = isFlat ? 0.05 : isLarge ? 0.55 : 0.4;
  const radius = isFlat ? 0.3 : isLarge ? 0.25 : 0.2;
  return (
    <mesh position={[offset[0], offset[1] + height / 2, offset[2]]} castShadow>
      <coneGeometry args={[radius, height, 12]} />
      <meshStandardMaterial color={color} roughness={0.6} />
    </mesh>
  );
}

function GoalFrame({ variant, color }: { variant: string; color: string }): React.JSX.Element {
  // FIFA-ish dimensions in metres for the supported variants. Keeps the
  // frame tube radius small so the field reads through.
  const dims = goalDims(variant);
  const { w, h } = dims;
  const half = w / 2;
  const tube = 0.06;
  const mat = <meshStandardMaterial color={color} roughness={0.4} />;
  return (
    <>
      {/* Left post */}
      <mesh position={[-half, h / 2, 0]} castShadow>
        <cylinderGeometry args={[tube, tube, h, 8]} />
        {mat}
      </mesh>
      {/* Right post */}
      <mesh position={[half, h / 2, 0]} castShadow>
        <cylinderGeometry args={[tube, tube, h, 8]} />
        {mat}
      </mesh>
      {/* Crossbar */}
      <mesh position={[0, h, 0]} rotation={[0, 0, Math.PI / 2]} castShadow>
        <cylinderGeometry args={[tube, tube, w, 8]} />
        {mat}
      </mesh>
    </>
  );
}

function goalDims(variant: string): { w: number; h: number } {
  switch (variant) {
    case 'goal_mini': return { w: 1.1, h: 0.55 };
    case 'goal_small': return { w: 1.6, h: 0.8 };
    case 'goal_7v7': return { w: 3.7, h: 1.65 };
    case 'goal_9v9': return { w: 5.0, h: 2.0 };
    case 'goal_full': return { w: 7.32, h: 2.44 };
    case 'crossbar': return { w: 7.32, h: 2.44 }; // just the bar — same dim
    default: return { w: 1.6, h: 0.8 };
  }
}

function ObstacleMesh({ variant, color }: { variant: string; color: string }): React.JSX.Element {
  const mat = <meshStandardMaterial color={color} roughness={0.6} />;
  switch (variant) {
    case 'hurdle_mini':
      return (
        <mesh position={[0, 0.15, 0]} castShadow>
          <boxGeometry args={[1.1, 0.3, 0.05]} />
          {mat}
        </mesh>
      );
    case 'hurdle_med':
      return (
        <mesh position={[0, 0.25, 0]} castShadow>
          <boxGeometry args={[1.1, 0.5, 0.05]} />
          {mat}
        </mesh>
      );
    case 'agility_ladder':
      return (
        <mesh position={[0, 0.01, 0]} castShadow>
          <boxGeometry args={[0.55, 0.02, 3.6]} />
          {mat}
        </mesh>
      );
    case 'bench':
      return (
        <mesh position={[0, 0.25, 0]} castShadow>
          <boxGeometry args={[1.8, 0.5, 0.4]} />
          {mat}
        </mesh>
      );
    case 'rebounder':
      return (
        <mesh position={[0, 0.55, 0]} castShadow>
          <boxGeometry args={[1.2, 1.1, 0.05]} />
          {mat}
        </mesh>
      );
    default:
      return <></>;
  }
}
